import React from 'react';
import { Button, DatePicker, Input, Select } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { OrderFilters, DateRangeValue } from './types';

const { RangePicker } = DatePicker;

type Props = {
  filters: OrderFilters;
  selectedCount: number;
  onChange: <K extends keyof OrderFilters>(key: K, value: OrderFilters[K]) => void;
  onReset: () => void;
  onBatchSync: () => void;
};

const OrderFiltersBar: React.FC<Props> = ({ filters, selectedCount, onChange, onReset, onBatchSync }) => (
  <div className="orders-toolbar">
    <div className="orders-filter-panel">
      <div className="orders-filter-fields">
        <span className="orders-filter-title-inline">筛选</span>
        <div className="orders-filter-control orders-filter-control-wide">
          <Input
            placeholder="订单/姓名/手机号/身份证/平台单号"
            prefix={<SearchOutlined />}
            value={filters.keyword}
            onChange={(event) => onChange('keyword', event.target.value)}
            allowClear
          />
        </div>
        <div className="orders-filter-control orders-filter-control-date">
          <RangePicker placeholder={['开始时间', '结束时间']} value={filters.dateRange} onChange={(value) => onChange('dateRange', value as DateRangeValue)} />
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="来源" allowClear value={filters.sourcePlatform || undefined} onChange={(value) => onChange('sourcePlatform', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="haoyi">号易</Select.Option>
            <Select.Option value="172">172</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="订单状态" allowClear value={filters.status || undefined} onChange={(value) => onChange('status', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="pending">处理中</Select.Option>
            <Select.Option value="success">成功</Select.Option>
            <Select.Option value="failed">失败</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="业务状态" allowClear value={filters.clientStatus || undefined} onChange={(value) => onChange('clientStatus', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="applying">申请中</Select.Option>
            <Select.Option value="opening">开卡中</Select.Option>
            <Select.Option value="shipping">运输中</Select.Option>
            <Select.Option value="activated">已激活</Select.Option>
            <Select.Option value="closed">失败/取消</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-medium">
          <Input placeholder="平台订单号" value={filters.platformOrderNo} onChange={(event) => onChange('platformOrderNo', event.target.value)} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-medium">
          <Input placeholder="平台状态" value={filters.platformStatus} onChange={(event) => onChange('platformStatus', event.target.value)} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-mini">
          <Input placeholder="产品ID" value={filters.productId} onChange={(event) => onChange('productId', event.target.value.replace(/[^\d]/g, ''))} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-medium">
          <Input placeholder="外部产品ID" value={filters.sourceProductId} onChange={(event) => onChange('sourceProductId', event.target.value)} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-mini">
          <Input placeholder="姓名" value={filters.name} onChange={(event) => onChange('name', event.target.value)} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-medium">
          <Input placeholder="身份证" value={filters.idCard} onChange={(event) => onChange('idCard', event.target.value)} allowClear />
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="物流" allowClear value={filters.hasTrackingNo || undefined} onChange={(value) => onChange('hasTrackingNo', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="true">有物流</Select.Option>
            <Select.Option value="false">无物流</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="卡号" allowClear value={filters.hasCardNumber || undefined} onChange={(value) => onChange('hasCardNumber', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="true">有卡号</Select.Option>
            <Select.Option value="false">无卡号</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-medium">
          <Select placeholder="异常状态" allowClear value={filters.exceptionStatus || undefined} onChange={(value) => onChange('exceptionStatus', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="pending_auto_retry">等待自动重提</Select.Option>
            <Select.Option value="needs_manual_retry">需要手动重提</Select.Option>
            <Select.Option value="sync_failed">同步失败</Select.Option>
            <Select.Option value="manual_open">人工打开</Select.Option>
            <Select.Option value="resolved">已处理</Select.Option>
          </Select>
        </div>
        <div className="orders-filter-control orders-filter-control-small">
          <Select placeholder="异常队列" allowClear value={filters.exceptionOnly || undefined} onChange={(value) => onChange('exceptionOnly', value || '')}>
            <Select.Option value="">全部</Select.Option>
            <Select.Option value="true">只看异常</Select.Option>
          </Select>
        </div>
      </div>

      <div className="orders-filter-actions">
        <Button size="small" icon={<ReloadOutlined />} onClick={onReset}>
          重置
        </Button>
        <Button size="small" type="primary" icon={<ReloadOutlined />} disabled={selectedCount === 0} onClick={onBatchSync}>
          同步选中{selectedCount > 0 ? `（${selectedCount}）` : ''}
        </Button>
      </div>
    </div>
  </div>
);

export default OrderFiltersBar;
